import { useCallback, useEffect, useState } from "react";
import { api } from "../api";

/**
 * SECRET-LANE (POST-GATE-SECRET-LANE-INCIDENT §4) — sichere Owner-Eingabe
 * für ein freigegebenes Credential. Reale Produkt-Oberfläche auf dem
 * bestehenden Vault-Broker (kein zweiter Secret-Store):
 *   - masked Eingabe (type=password, autocomplete aus, kein Echo)
 *   - Wert verlässt das Feld ausschließlich als POST an die bounded Route;
 *     nach dem Submit wird es geleert, der Server antwortet NUR mit Receipt
 *   - kein Wert in Logs/Evidence/Queue — Audit speichert Schlüssel+Label
 *   - Widerruf/Rotation über den bestehenden Secrets-Kanal
 * Sichtbar nur bei offenen Requests (REQUIRED/PROVIDED) — sonst rendert die
 * Komponente nichts (keine dauerhafte Fläche).
 */
export function SecretProvisionPanel(): JSX.Element | null {
  const [requests, setRequests] = useState<Array<{ id: string; secretLabel: string; status: string; workitemId: string }>>([]);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<Record<string, { ok: boolean; text: string }>>({});
  const [error, setError] = useState<string | null>(null);

  const lade = useCallback(async (): Promise<void> => {
    try {
      const r = await api.secretRequests();
      setRequests(r.items);
    } catch {
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    void lade();
    const timer = setInterval(() => void lade(), 15_000);
    return () => clearInterval(timer);
  }, [lade]);

  if (requests.length === 0) return null;

  const hinterlege = async (id: string): Promise<void> => {
    const wert = (werte[id] ?? "").trim();
    if (!wert || busy !== null) return;
    setBusy(id);
    setError(null);
    try {
      const r = await api.secretProvision(id, wert);
      setReceipt((alt) => ({ ...alt, [id]: { ok: true, text: `${r.hinweis} (Referenz ${r.fingerprint})` } }));
      setWerte((alt) => ({ ...alt, [id]: "" }));
      await lade();
    } catch (e) {
      setReceipt((alt) => ({ ...alt, [id]: { ok: false, text: e instanceof Error ? e.message : "Hinterlegen fehlgeschlagen" } }));
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card p-4" aria-label="Credential sicher hinterlegen" data-testid="secret-provision-panel">
      <h3 className="text-sm font-semibold">Credential sicher hinterlegen</h3>
      <p className="mt-1 text-xs text-jarvis-muted">
        Freigabe erteilt — Credential noch erforderlich. Der Wert wird verschlüsselt im Secret-Store hinterlegt, nie
        angezeigt, nie in Evidence/Queue geschrieben. Widerruf und Rotation laufen über den bestehenden Secrets-Kanal
        (Settings → Secrets). Nach dem Hinterlegen laufen Validierung und Worker-Einrichtung automatisch.
      </p>
      {error && <p className="mt-2 text-xs text-rose-300">{error}</p>}
      <div className="mt-3 space-y-3">
        {requests.map((anfrage) => (
          <div key={anfrage.id} className="rounded-md border border-jarvis-border/60 p-3" data-testid={`secret-request-${anfrage.id}`}>
            <div className="text-xs font-medium">{anfrage.secretLabel}</div>
            <div className="font-mono text-[10px] text-jarvis-muted">Item {anfrage.workitemId}</div>
            {anfrage.status === "REQUIRED" ? (
              <form
                className="mt-2 flex flex-wrap items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  void hinterlege(anfrage.id);
                }}
              >
                <input
                  type="password"
                  autoComplete="new-password"
                  spellCheck={false}
                  className="min-h-11 flex-1 min-w-[12rem] rounded-md border border-jarvis-border bg-jarvis-bg/70 px-3 text-sm outline-none focus:border-jarvis-accent/60"
                  placeholder="Credential-Wert (maskiert, wird nie angezeigt)"
                  aria-label={`Credential-Wert für ${anfrage.secretLabel}`}
                  value={werte[anfrage.id] ?? ""}
                  onChange={(e) => setWerte((alt) => ({ ...alt, [anfrage.id]: e.target.value }))}
                  data-nonce={anfrage.id}
                />
                <button
                  type="submit"
                  className="min-h-11 rounded-md border border-teal-200/50 bg-teal-300/80 px-4 text-sm font-semibold text-slate-950 disabled:opacity-50"
                  disabled={busy !== null || (werte[anfrage.id] ?? "").trim().length === 0}
                >
                  {busy === anfrage.id ? "Hinterlegt…" : "Sicher hinterlegen"}
                </button>
              </form>
            ) : (
              <p className="mt-2 text-xs text-jarvis-muted">Hinterlegt — Validierung und Worker-Einrichtung laufen automatisch.</p>
            )}
            {receipt[anfrage.id] !== undefined && receipt[anfrage.id] !== null && (
              <p className={`mt-2 text-xs ${receipt[anfrage.id]?.ok ? "text-emerald-300" : "text-rose-300"}`}>
                {receipt[anfrage.id]?.text}
              </p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
